<template>
  <div class="buyer-products-page market-project market-project-v2">
    <!-- Reuse the same layout wrapper as Seller pages to inherit styling -->
    <div class="seller-products-page__layout">
      <div class="accFeed__wrapper matchmaking__wrapper">
        <!-- Sidebar -->
        <div class="accFeed__sidebar matchmaking__sidebar">
          <div class="search__form-container">
            <h3>Search</h3>
            <div class="search__form">
              <input
                v-model.trim="searchInput"
                type="text"
                class="search__input"
                placeholder="Type to search"
              />
              <button type="button" class="search__form-btn">
                <span class="search__form-icon"></span>
              </button>
            </div>
          </div>

          <div class="accordion" id="accordionMarketplaceBuyer">
            <div class="card sidebar">
              <div class="card-header" id="headingMarketplaceBuyer">
                <button
                  class="btn-accordion"
                  type="button"
                  data-toggle="collapse"
                  data-target="#collapseMarketplaceBuyer"
                  aria-expanded="true"
                  aria-controls="collapseMarketplaceBuyer"
                >
                  <span>Marketplace</span>
                </button>
              </div>

              <div
                id="collapseMarketplaceBuyer"
                class="collapse show"
                aria-labelledby="headingMarketplaceBuyer"
                data-parent="#accordionMarketplaceBuyer"
              >
                <div class="card-body">
                  <ul class="channel__list">
                    <li class="channel__item">
                      <span
                        class="channel__link"
                        :class="{ active: !marketplaceSelectedId }"
                        @click="selectMarketplace(null)"
                        style="cursor: pointer;"
                      >
                        All
                      </span>
                    </li>

                    <li
                      v-for="m in marketplaces"
                      :key="m.id"
                      class="channel__item"
                    >
                      <span
                        class="channel__link"
                        :class="{ active: String(marketplaceSelectedId) === String(m.id) }"
                        @click="selectMarketplace(m)"
                        style="cursor: pointer;"
                      >
                        {{ m.name }}
                        <MarketplaceUnreadBadge :count="marketplaceBulb(m.id)" />
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Keep FAQ link as in the design mockups -->
          <div class="sidebar-block">
            <a class="link" href="">FAQ</a>
          </div>
        </div>

        <!-- Content -->
        <div class="accFeed__content matchmaking__content">
          <div class="offers">
          <div class="offers-status">
            <h3>
              <template v-if="isSavedView">
                You have: <strong>{{ filteredProducts.length }}</strong> saved products
              </template>
              <template v-else>
                <strong>{{ filteredProducts.length }}</strong>
                products are shown
                <template v-if="marketplaceSelectedName">
                  in <strong>the {{ marketplaceSelectedName }} marketplace</strong>
                </template>
              </template>
            </h3>

            <div class="offers-status__filters">
              <div>
                <a href="#" @click.prevent="statusFilter = 'all'">
                  <span class="status all" :class="{ active: statusFilter === 'all' }">All</span>
                </a>
                <a href="#" @click.prevent="statusFilter = 'saved'">
                  <span class="status saved" :class="{ active: statusFilter === 'saved' }">
                    Saved
                  </span>
                </a>
                <a href="#" @click.prevent="statusFilter = 'approved'">
                  <span class="status approved" :class="{ active: statusFilter === 'approved' }">
                    Approved
                  </span>
                </a>
                <a href="#" @click.prevent="statusFilter = 'counteroffered'">
                  <span class="status counteroffered"
                        :class="{ active: statusFilter === 'counteroffered' }"
                  >
                    counter offered
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div v-if="isLoading" class="my-products-loader">Loading...</div>

          <div v-else>
            <div
              v-if="!filteredProducts.length"
              class="no-content"
            >
              <p class="mb-0">No products found.</p>
            </div>

            <div
              v-else
              class="accordion offers__box"
              id="accordionBuyerProducts"
            >
              <BuyerProductCard
                v-for="p in filteredProducts"
                :key="p.id"
                :product="p"
                accordion-id="accordionBuyerProducts"
                @saved="onProductSaved"
                @unsaved="onProductUnsaved"
                @approved="onProductApproved"
              />
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import exception from '@/mixins/exception';
import MatchmakingService from '@/services/MatchmakingService';
import BuyerProductCard from '@/components/matchmaking/BuyerProductCard.vue';
import MarketplaceUnreadBadge from '@/components/matchmaking/MarketplaceUnreadBadge.vue';

export default {
  name: 'BuyerProducts',

  components: {
    BuyerProductCard,
    MarketplaceUnreadBadge,
  },

  mixins: [exception],

  data() {
    return {
      marketplaces: [],
      marketplaceSelectedId: null,
      products: [],
      isLoading: false,
      searchInput: '',
      statusFilter: 'all',
    };
  },

  computed: {
    currentView() {
      return String(this.$route.query.view || 'products');
    },
    isSavedView() {
      return this.currentView === 'saved-products';
    },
    isProductsView() {
      return !this.isSavedView;
    },
    marketplaceSelectedName() {
      if (!this.marketplaceSelectedId) return '';
      const m = this.marketplaces.find((x) => x.id === this.marketplaceSelectedId);
      return m ? m.name : '';
    },

    filteredProducts() {
      const input = String(this.searchInput || '').trim().toLowerCase();
      const list = Array.isArray(this.products) ? this.products : [];

      const bySearch = !input
        ? list
        : list.filter((p) => {
          const name = String(p?.name || '').toLowerCase();
          const company = String(p?.company_name || '').toLowerCase();
          const country = String(p?.company_country?.name || p?.company_country?.iso || p?.company_country || '').toLowerCase();

          const inData = Array.isArray(p?.data)
            && p.data.some((f) => {
              const fieldName = String(f?.name || '').toLowerCase();
              const fieldVal = String(f?.initValue ?? f?.value ?? '').toLowerCase();
              return fieldName.includes(input) || fieldVal.includes(input);
            });

          return name.includes(input)
            || company.includes(input)
            || country.includes(input)
            || inData;
        });

      const filter = String(this.statusFilter || 'all');
      if (filter === 'all') return bySearch;

      if (filter === 'saved') {
        return bySearch.filter((p) => Boolean(p?.saved));
      }

      if (filter === 'counteroffered') {
        return bySearch.filter((p) => this.hasCounterOffers(p));
      }

      if (filter === 'approved') {
        return bySearch.filter((p) => this.hasApprovedOffer(p));
      }

      return bySearch;
    },
  },

  watch: {
    marketplaceSelectedId: {
      immediate: true,
      handler() {
        this.fetchProducts();
      },
    },

    '$route.query.view': {
      immediate: true,
      handler(view) {
        // If user clicks "Saved products" submenu, make the Saved filter active.
        if (String(view || '') === 'saved-products') {
          this.statusFilter = 'saved';
        } else if (this.statusFilter === 'saved') {
          // Keep user's manual choice if they changed it.
          this.statusFilter = 'all';
        }
      },
    },
    '$route.query.marketplace_id': {
      immediate: true,
      handler(raw) {
        const parsed = this.parseMarketplaceQueryId(raw);
        if (String(parsed || '') !== String(this.marketplaceSelectedId || '')) {
          this.marketplaceSelectedId = parsed;
        }
      },
    },
  },

  created() {
    this.fetchMarketplaces();
  },

  methods: {
    marketplaceBulb(marketplaceId) {
      return this.$store.getters.marketplaceBulb('buyer', marketplaceId);
    },
    hasCounterOffers(product) {
      const count = Number(product?.counter_offers_count ?? product?.offers_count ?? 0);
      if (count > 0) return true;
      return Array.isArray(product?.offers) && product.offers.length > 0;
    },
    hasApprovedOffer(product) {
      const statusName = String(product?.status_name || product?.status?.name || '').toLowerCase();
      if (statusName.includes('approved')) return true;
      if (Number(product?.status?.id ?? product?.status) === 2) return true;

      const offers = Array.isArray(product?.offers) ? product.offers : [];
      return offers.some((offer) => {
        const offerStatusName = String(offer?.status?.name || offer?.status_name || '').toLowerCase();
        if (offerStatusName.includes('approved')) return true;
        const offerStatusId = Number(offer?.status?.id ?? offer?.status);
        return offerStatusId === 2 || offerStatusId === 3;
      });
    },
    async fetchMarketplaces() {
      try {
        this.$store.commit('SET_IS_LOADING', true);
        const { data } = await MatchmakingService.marketplaces();
        this.marketplaces = Array.isArray(data) ? data : [];
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        this.handleException(e);
      } finally {
        this.$store.commit('SET_IS_LOADING', false);
      }
    },

    parseMarketplaceQueryId(raw) {
      if (raw === undefined || raw === null || raw === '') return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },

    syncMarketplaceQuery(nextMarketplaceId) {
      const currentRaw = this.$route.query.marketplace_id;
      const current = this.parseMarketplaceQueryId(currentRaw);
      const next = this.parseMarketplaceQueryId(nextMarketplaceId);
      if (String(current || '') === String(next || '')) return;

      const query = { ...this.$route.query };
      if (next) {
        query.marketplace_id = String(next);
      } else {
        delete query.marketplace_id;
      }

      this.$router.replace({ query }).catch(() => {});
    },

    selectMarketplace(marketplace) {
      this.marketplaceSelectedId = marketplace ? marketplace.id : null;
      this.syncMarketplaceQuery(this.marketplaceSelectedId);
    },

    async fetchProducts() {
      this.isLoading = true;
      try {
        const query = {
          user: 'buyer',
        };

        if (this.marketplaceSelectedId) {
          query.marketplace_id = this.marketplaceSelectedId;
        }

        const { data } = await MatchmakingService.marketplaceProducts(query);
        this.products = Array.isArray(data) ? data : [];
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        this.handleException(e);
        this.products = [];
      } finally {
        this.isLoading = false;
      }
    },

    onProductSaved(product) {
      const p = this.products.find((x) => x && String(x.id) === String(product?.id));
      if (p) this.$set(p, 'saved', true);
    },

    onProductUnsaved(product) {
      const p = this.products.find((x) => x && String(x.id) === String(product?.id));
      if (p) this.$set(p, 'saved', false);
    },

    onProductApproved(product) {
      const idx = this.products.findIndex((x) => x && String(x.id) === String(product?.id));
      if (idx === -1) return;

      const current = this.products[idx] || {};
      const nextStatus = Number(product?.status);
      const normalizedStatus = Number.isFinite(nextStatus) && nextStatus > 0
        ? nextStatus
        : Number(current?.status) || 2;
      const normalizedStatusName = String(
        product?.status_name
        || product?.status?.name
        || (normalizedStatus === 2 ? 'approved' : current?.status_name)
        || '',
      );

      this.$set(this.products, idx, {
        ...current,
        ...(product && typeof product === 'object' ? product : {}),
        status: normalizedStatus,
        status_name: normalizedStatusName,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.my-products-loader {
  padding: 30px 0;
}

.no-content {
  padding: 30px 0;
}
</style>
